import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Avatar,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  IconButton,
  Alert,
  Autocomplete,
  Divider,
  alpha,
  useTheme,
  Snackbar,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TableSortLabel,
} from '@mui/material';
import {
  PersonAddRounded,
  DeleteRounded,
  EditRounded,
  LinkRounded,
  LinkOffRounded,
  AdminPanelSettingsRounded,
  SchoolRounded,
  BadgeRounded,
  AccountBalanceRounded,
  PeopleRounded,
  CheckRounded,
  GroupsRounded,
  MarkEmailReadRounded,
  SearchRounded,
  FilterListRounded,
  ClearRounded,
  TimelineRounded,
} from '@mui/icons-material';
import {
  collection,
  doc,
  documentId,
  endAt,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
  where,
} from 'firebase/firestore';
import { db, auth } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { usersApi, type TenantRole, type MembershipRecord } from '../../lib/api/users.api';
import { useConfirm } from '../../components/ConfirmProvider';
import { ALL_ROLES, MODULE_LABEL_KEYS, type RoleMeta } from '../../config/permissions.config';
import { errorText } from '../../lib/error-text';
import { useAuth } from '../../contexts/auth.context';
import { UserActivityDialog } from './components/UserActivityDialog';
import {
  PERSON_LIST_CAP,
  PERSON_SEARCH_LIMIT,
  canSave,
  matchesSearch,
  openDialogState,
  planSave,
  selectorOptions,
  searchQueries,
  toPersonOption,
  toPersonOptions,
  type LinkedPerson,
  type PersonListState,
  type PersonOption,
} from './users-person-link';

// ─── Role icon map ────────────────────────────────────────────────────────────

const ROLE_ICONS: Record<string, React.ReactNode> = {
  tenantAdmin: <AdminPanelSettingsRounded />,
  registrar: <BadgeRounded />,
  academicDean: <SchoolRounded />,
  faculty: <SchoolRounded />,
  reviewer: <PeopleRounded />,
  mentor: <PeopleRounded />,
  finance: <AccountBalanceRounded />,
  student: <SchoolRounded />,
};

const ROLE_COLORS: Record<string, string> = {
  tenantAdmin: 'error',
  registrar: 'warning',
  academicDean: 'primary',
  faculty: 'info',
  reviewer: 'secondary',
  mentor: 'success',
  finance: 'success',
  student: 'default',
};

// ─── RoleCard ─────────────────────────────────────────────────────────────────

function RoleCard({
  role,
  selected,
  onToggle,
}: {
  role: RoleMeta;
  selected: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('sis');
  const theme = useTheme();
  const pc = theme.palette.primary.main;

  return (
    <Box
      onClick={onToggle}
      sx={{
        position: 'relative',
        border: '1.5px solid',
        borderColor: selected ? pc : 'divider',
        borderRadius: 2,
        p: 1.5,
        cursor: 'pointer',
        bgcolor: selected ? alpha(pc, 0.08) : 'action.hover',
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: selected ? pc : alpha(pc, 0.5),
          bgcolor: selected ? alpha(pc, 0.12) : alpha(pc, 0.04),
        },
      }}
    >
      {/* Selected checkmark */}
      {selected && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 18,
            height: 18,
            borderRadius: '50%',
            bgcolor: pc,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckRounded sx={{ fontSize: 12, color: '#fff' }} />
        </Box>
      )}

      {/* Header */}
      <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
        <Box sx={{ color: selected ? pc : 'text.secondary', display: 'flex', fontSize: 18 }}>
          {ROLE_ICONS[role.value] ?? <BadgeRounded />}
        </Box>
        <Typography
          variant="body2"
          fontWeight={700}
          color={selected ? pc : 'text.primary'}
          lineHeight={1.2}
        >
          {t(role.labelKey)}
        </Typography>
      </Stack>

      {/* Description */}
      <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.4} mb={1}>
        {t(role.descriptionKey)}
      </Typography>

      {/* Module chips */}
      <Stack direction="row" flexWrap="wrap" gap={0.4}>
        {role.modules.slice(0, 4).map((mod) => (
          <Chip
            key={mod}
            label={t(MODULE_LABEL_KEYS[mod])}
            size="small"
            sx={{ fontSize: 9, height: 18, opacity: selected ? 1 : 0.6 }}
            color={selected ? 'primary' : 'default'}
            variant={selected ? 'outlined' : 'filled'}
          />
        ))}
        {role.modules.length > 4 && (
          <Chip
            label={t('usersPage.moreModules', { count: role.modules.length - 4 })}
            size="small"
            sx={{ fontSize: 9, height: 18, opacity: 0.6 }}
          />
        )}
      </Stack>
    </Box>
  );
}

// ─── Firestore membership reader ──────────────────────────────────────────────

interface RawMembership {
  uid: string;
  email?: string;
  displayName?: string;
  roles: TenantRole[];
  status: string;
  personId?: string;
  joinedAt?: { toDate: () => Date };
}

/** A "system user" has at least one non-student role. Pure students belong to the
 *  Personas page, not the "Usuarios del Sistema" (staff access) list. */
function isSystemUser(m: MembershipRecord): boolean {
  return m.roles.some((r) => r !== 'student');
}

async function fetchMemberships(tenantId: string): Promise<MembershipRecord[]> {
  // Some memberships (e.g. seeded) don't denormalize email/displayName — only
  // uid + personId. Hydrate the display from the linked Person so the list shows
  // real names, not the uid. The Persons are read BY ID: a seminary can have
  // far more people than accounts, and a capped scan of `people` left names out.
  const mSnap = await getDocs(collection(db, `tenants/${tenantId}/memberships`));
  const personIds = [
    ...new Set(
      mSnap.docs
        .map((d) => (d.data() as RawMembership).personId)
        .filter((id): id is string => !!id),
    ),
  ];
  const personById = new Map<string, { name: string; email: string }>();
  for (let i = 0; i < personIds.length; i += 30) {
    const pSnap = await getDocs(
      query(
        collection(db, `tenants/${tenantId}/people`),
        where(documentId(), 'in', personIds.slice(i, i + 30)),
      ),
    );
    for (const p of pSnap.docs) {
      const data = p.data() as Record<string, unknown>;
      const name =
        `${(data['firstName'] as string) ?? ''} ${(data['lastName'] as string) ?? ''}`.trim();
      personById.set(p.id, { name, email: (data['email'] as string) ?? '' });
    }
  }
  return mSnap.docs.map((m) => {
    const d = m.data() as RawMembership;
    const uid = d.uid ?? m.id;
    const person = d.personId ? personById.get(d.personId) : undefined;
    // The signed-in user's own row: fall back to their Auth profile when the
    // membership isn't linked to a Person and has no denormalized email/name
    // (e.g. the tenant admin, created outside the invite flow).
    const isSelf = auth.currentUser?.uid === uid;
    const selfEmail = isSelf ? (auth.currentUser?.email ?? '') : '';
    const selfName = isSelf ? (auth.currentUser?.displayName ?? '') : '';
    const email = d.email ?? person?.email ?? selfEmail;
    const displayName = d.displayName ?? (person?.name || selfName || email.split('@')[0] || uid);
    const record: MembershipRecord = {
      uid,
      email,
      displayName,
      roles: d.roles ?? [],
      status: (d.status ?? 'active') as MembershipRecord['status'],
    };
    if (d.personId) record.personId = d.personId;
    const joined = d.joinedAt?.toDate().toISOString();
    if (joined) record.joinedAt = joined;
    return record;
  });
}

// ─── Persons helper ───────────────────────────────────────────────────────────
// The rules live in users-person-link.ts; here, only the reads.

/** Up to PERSON_LIST_CAP people: the whole list, as always. More: `null`, and
 *  the selector searches instead of listing. */
async function fetchPersonOptions(tenantId: string): Promise<PersonOption[] | null> {
  const snap = await getDocs(
    query(collection(db, `tenants/${tenantId}/people`), limit(PERSON_LIST_CAP + 1)),
  );
  if (snap.size > PERSON_LIST_CAP) return null;
  return toPersonOptions(snap.docs.map((d) => ({ id: d.id, data: d.data() })));
}

/** The linked Person, always by id: never inferred from a capped list. */
async function fetchLinkedPerson(tenantId: string, personId: string): Promise<LinkedPerson> {
  try {
    const snap = await getDoc(doc(db, `tenants/${tenantId}/people/${personId}`));
    return snap.exists()
      ? { state: 'loaded', option: toPersonOption(snap.id, snap.data()) }
      : { state: 'unreadable' };
  } catch {
    return { state: 'unreadable' };
  }
}

/** Prefix queries by name, code or email, PERSON_SEARCH_LIMIT each. */
async function searchPeople(tenantId: string, text: string): Promise<PersonOption[]> {
  const col = collection(db, `tenants/${tenantId}/people`);
  const snaps = await Promise.all(
    searchQueries(text).map((q) =>
      getDocs(
        query(
          col,
          orderBy(q.field),
          startAt(q.prefix),
          endAt(`${q.prefix}\uf8ff`),
          limit(PERSON_SEARCH_LIMIT),
        ),
      ),
    ),
  );
  const byId = new Map<string, PersonOption>();
  for (const snap of snaps) {
    for (const d of snap.docs) {
      if (d.data()['deletedAt']) continue;
      if (!byId.has(d.id)) byId.set(d.id, toPersonOption(d.id, d.data()));
    }
  }
  return [...byId.values()]
    .filter((o) => matchesSearch(o, text))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// ─── Invite Dialog ────────────────────────────────────────────────────────────

/** Cargos institucionales disponibles al invitar un usuario.
 * Stable ids; las etiquetas/descripciones visibles se resuelven con
 * t('usersPage.institutionalRoles.<value>.{label,description}') en el render. */
const INVITE_INSTITUTIONAL_ROLES = [
  { value: 'docente' },
  { value: 'tutor' },
  { value: 'decano' },
  { value: 'academico' },
  { value: 'coordinador' },
  { value: 'capellan' },
  { value: 'staff' },
] as const;

function InviteDialog({
  open,
  tenantId,
  onClose,
  onDone,
}: {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('sis');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<TenantRole[]>([]);
  const [selectedInstitutional, setSelectedInstitutional] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function toggleRole(role: TenantRole) {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  function toggleInstitutional(role: string) {
    setSelectedInstitutional((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  async function handleSubmit() {
    if (!email.trim() || selectedRoles.length === 0) return;
    setLoading(true);
    setError('');
    try {
      await usersApi.invite({
        tenantId,
        email: email.trim(),
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        roles: selectedRoles,
        institutionalRoles: selectedInstitutional,
      });
      setEmail('');
      setFirstName('');
      setLastName('');
      setSelectedRoles([]);
      setSelectedInstitutional([]);
      onDone();
      onClose();
    } catch (e: unknown) {
      setError(errorText(e, t('usersPage.invite.error')));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PersonAddRounded color="primary" />
          {t('usersPage.invite.title')}
        </Stack>
        <Typography variant="body2" color="text.secondary" fontWeight={400} mt={0.5}>
          {t('usersPage.invite.subtitle')}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack gap={2.5}>
          {/* Contact info */}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              label={t('usersPage.invite.firstNameLabel')}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
            <TextField
              label={t('usersPage.invite.lastNameLabel')}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Stack>
          <TextField
            label={t('usersPage.invite.emailLabel')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            type="email"
            variant="outlined"
            size="small"
          />

          {/* Role selection — Acceso al sistema */}
          <Box>
            <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
              <AdminPanelSettingsRounded fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={700}>
                {t('usersPage.invite.systemAccess')}
              </Typography>
              {selectedRoles.length > 0 && (
                <Chip
                  label={t('usersPage.selectedCount', { count: selectedRoles.length })}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              {t('usersPage.invite.systemAccessHelp')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 1.5,
              }}
            >
              {ALL_ROLES.map((role) => (
                <RoleCard
                  key={role.value}
                  role={role}
                  selected={selectedRoles.includes(role.value as TenantRole)}
                  onToggle={() => toggleRole(role.value as TenantRole)}
                />
              ))}
            </Box>
          </Box>

          {/* Cargo institucional */}
          <Box>
            <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
              <GroupsRounded fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={700}>
                {t('usersPage.institutionalRole.title')}
              </Typography>
              {selectedInstitutional.length > 0 && (
                <Chip
                  label={t('usersPage.selectedCount', { count: selectedInstitutional.length })}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              {t('usersPage.institutionalRole.help')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' },
                gap: 1,
              }}
            >
              {INVITE_INSTITUTIONAL_ROLES.map((role) => {
                const isSelected = selectedInstitutional.includes(role.value);
                return (
                  <Box
                    key={role.value}
                    onClick={() => toggleInstitutional(role.value)}
                    sx={{
                      border: '1.5px solid',
                      borderColor: isSelected ? 'success.main' : 'divider',
                      borderRadius: 2,
                      p: 1.25,
                      cursor: 'pointer',
                      bgcolor: isSelected ? alpha('#2e7d32', 0.12) : 'action.hover',
                      transition: 'all 0.15s ease',
                      position: 'relative',
                      '&:hover': { borderColor: 'success.main' },
                    }}
                  >
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckRounded sx={{ fontSize: 9, color: '#fff' }} />
                      </Box>
                    )}
                    <Typography variant="caption" fontWeight={700} lineHeight={1.2} display="block">
                      {t(`usersPage.institutionalRoles.${role.value}.label`)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      lineHeight={1.2}
                      display="block"
                      mt={0.25}
                    >
                      {t(`usersPage.institutionalRoles.${role.value}.description`)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          {t('usersPage.actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !email.trim() || selectedRoles.length === 0}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : <PersonAddRounded />
          }
          sx={{ minWidth: 120 }}
        >
          {loading ? t('usersPage.invite.sending') : t('usersPage.invite.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit Roles Dialog ────────────────────────────────────────────────────────

function EditRolesDialog({
  member,
  tenantId,
  members,
  onClose,
  onDone,
}: {
  member: MembershipRecord | null;
  tenantId: string;
  /** All current members so we can exclude already-linked persons from the selector */
  members: MembershipRecord[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('sis');
  const [selectedRoles, setSelectedRoles] = useState<TenantRole[]>(member?.roles ?? []);
  const [selectedInstitutional, setSelectedInstitutional] = useState<string[]>([]);
  const [list, setList] = useState<PersonListState>({ mode: 'loading' });
  const [linked, setLinked] = useState<LinkedPerson>({ state: 'loading' });
  const [selectedPerson, setSelectedPerson] = useState<PersonOption | null>(null);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<PersonOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Al abrir (o cambiar de cuenta) se parte de cero: nada de la cuenta
  // anterior puede viajar en el guardado. Las dos lecturas son independientes:
  // si falla la lista, el vínculo leído por id se muestra igual.
  useEffect(() => {
    const fresh = openDialogState();
    setSelectedRoles(member?.roles ?? []);
    setError('');
    setSearchText('');
    setSearchResults([]);
    setLinked(fresh.linked);
    setList(fresh.list);
    setSelectedPerson(fresh.selectedPerson);
    setSelectedInstitutional(fresh.selectedInstitutional);
    if (!member) return;
    let cancelled = false;
    fetchPersonOptions(tenantId).then(
      (options) => {
        if (!cancelled) setList(options === null ? { mode: 'search' } : { mode: 'list', options });
      },
      () => {
        if (!cancelled) setList({ mode: 'unavailable' });
      },
    );
    (member.personId
      ? fetchLinkedPerson(tenantId, member.personId)
      : Promise.resolve<LinkedPerson>({ state: 'none' })
    ).then((link) => {
      if (cancelled) return;
      setLinked(link);
      const current = link.state === 'loaded' ? link.option : null;
      setSelectedPerson(current);
      // Pre-load institutional roles from the linked person
      setSelectedInstitutional(current?.institutionalRoles ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [member, tenantId]);

  // Persons already linked to a DIFFERENT membership are not offered.
  const takenPersonIds = useMemo(
    () =>
      new Set(members.filter((m) => m.uid !== member?.uid && m.personId).map((m) => m.personId!)),
    [members, member],
  );

  // Search mode (big seminary): prefix queries as the user types.
  useEffect(() => {
    if (list.mode !== 'search' || searchText.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      searchPeople(tenantId, searchText)
        .then((found) => {
          if (!cancelled) setSearchResults(found);
        })
        .catch(() => {
          if (!cancelled) setSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [list.mode, searchText, tenantId]);

  const personOptions = useMemo(
    () => selectorOptions({ list, searchResults, takenPersonIds, selectedPerson }),
    [list, searchResults, takenPersonIds, selectedPerson],
  );

  const linkUnreadable = !!member?.personId && linked.state === 'unreadable';
  const saveEnabled = canSave({ linked, list, saving, selectedRoles });

  function toggleRole(role: TenantRole) {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  function toggleInstitutional(role: string) {
    setSelectedInstitutional((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  async function handleSave() {
    if (!member || !saveEnabled) return;
    setSaving(true);
    setError('');
    try {
      const plan = planSave({
        previousPersonId: member.personId ?? null,
        linked,
        selectedPerson,
        selectedRoles,
        selectedInstitutional,
      });
      await usersApi.updateRoles({
        tenantId,
        uid: member.uid,
        roles: plan.roles as TenantRole[],
        ...(plan.institutionalRoles ? { institutionalRoles: plan.institutionalRoles } : {}),
      });
      if (plan.personLink !== undefined) {
        await usersApi.updatePersonLink({ tenantId, uid: member.uid, personId: plan.personLink });
      }
      onDone();
      onClose();
    } catch (e: unknown) {
      setError(errorText(e, t('usersPage.edit.error')));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={!!member}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
            {(member?.displayName ?? member?.email ?? '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography fontWeight={700} lineHeight={1.2}>
              {member?.displayName || member?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {member?.email}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack gap={3}>
          {/* Roles */}
          <Box>
            <Stack direction="row" alignItems="baseline" gap={1} mb={1.5}>
              <Typography variant="body2" fontWeight={700}>
                {t('usersPage.edit.rolesAndAccess')}
              </Typography>
              {selectedRoles.length > 0 && (
                <Chip
                  label={t('usersPage.selectedCount', { count: selectedRoles.length })}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 1.5,
              }}
            >
              {ALL_ROLES.map((role) => (
                <RoleCard
                  key={role.value}
                  role={role}
                  selected={selectedRoles.includes(role.value as TenantRole)}
                  onToggle={() => toggleRole(role.value as TenantRole)}
                />
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Cargo institucional */}
          <Box>
            <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
              <GroupsRounded fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={700}>
                {t('usersPage.institutionalRole.title')}
              </Typography>
              {selectedInstitutional.length > 0 && (
                <Chip
                  label={t('usersPage.selectedCount', { count: selectedInstitutional.length })}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              {t('usersPage.institutionalRole.help')}
            </Typography>
            {linkUnreadable && (
              <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
                {t('usersPage.institutionalRole.unreadable')}
              </Alert>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' },
                gap: 1,
                // Nothing shown, nothing editable: the save leaves them as they are.
                ...(linkUnreadable ? { opacity: 0.5, pointerEvents: 'none' } : {}),
              }}
              aria-disabled={linkUnreadable || undefined}
            >
              {INVITE_INSTITUTIONAL_ROLES.map((role) => {
                const isSelected = selectedInstitutional.includes(role.value);
                return (
                  <Box
                    key={role.value}
                    onClick={() => toggleInstitutional(role.value)}
                    sx={{
                      border: '1.5px solid',
                      borderColor: isSelected ? 'success.main' : 'divider',
                      borderRadius: 2,
                      p: 1.25,
                      cursor: 'pointer',
                      bgcolor: isSelected ? alpha('#2e7d32', 0.12) : 'action.hover',
                      transition: 'all 0.15s ease',
                      position: 'relative',
                      '&:hover': { borderColor: 'success.main' },
                    }}
                  >
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckRounded sx={{ fontSize: 9, color: '#fff' }} />
                      </Box>
                    )}
                    <Typography variant="caption" fontWeight={700} lineHeight={1.2} display="block">
                      {t(`usersPage.institutionalRoles.${role.value}.label`)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      lineHeight={1.2}
                      display="block"
                      mt={0.25}
                    >
                      {t(`usersPage.institutionalRoles.${role.value}.description`)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Divider />

          {/* Person link */}
          <Box>
            <Typography variant="body2" fontWeight={700} mb={0.5}>
              {t('usersPage.personLink.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              {t('usersPage.personLink.help')}
            </Typography>
            {linked.state === 'loading' ? (
              <CircularProgress size={20} color="primary" />
            ) : linkUnreadable ? (
              // The linked Person could not be read: say so, and keep the link
              // (and its institutional roles) untouched on save.
              <Alert severity="warning" variant="outlined">
                {t('usersPage.personLink.unreadable')}
              </Alert>
            ) : (
              <Autocomplete
                options={personOptions}
                value={selectedPerson}
                onChange={(_, val) => setSelectedPerson(val)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                // Big seminary: the options ARE the search results.
                {...(list.mode === 'unavailable'
                  ? { noOptionsText: t('usersPage.personLink.listUnavailable') }
                  : {})}
                {...(list.mode === 'search'
                  ? {
                      filterOptions: (x: PersonOption[]) => x,
                      onInputChange: (_: unknown, value: string, reason: string) => {
                        if (reason === 'input') setSearchText(value);
                      },
                      loading: searching,
                      noOptionsText:
                        searchText.trim().length < 2
                          ? t('usersPage.personLink.typeToSearch')
                          : t('usersPage.personLink.noMatches'),
                    }
                  : {})}
                renderInput={(params) => (
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  <TextField
                    {...(params as any)}
                    label={t('usersPage.personLink.fieldLabel')}
                    placeholder={t(
                      list.mode === 'search'
                        ? 'usersPage.personLink.searchByNameOrCode'
                        : 'usersPage.personLink.searchPlaceholder',
                    )}
                    size="small"
                    variant="outlined"
                  />
                )}
                clearIcon={<LinkOffRounded fontSize="small" />}
              />
            )}
            {selectedPerson && (
              <Stack direction="row" alignItems="center" gap={0.5} mt={0.75}>
                <LinkRounded sx={{ fontSize: 14 }} color="success" />
                <Typography variant="caption" color="success.main">
                  {t('usersPage.personLink.willLink', { label: selectedPerson.label })}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          {t('usersPage.actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!saveEnabled}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ minWidth: 120 }}
        >
          {saving ? t('usersPage.actions.saving') : t('usersPage.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Role status chip helper ──────────────────────────────────────────────────

function StatusChip({ status }: { status: string }) {
  const { t } = useTranslation('sis');
  const isActive = status === 'active';
  const isSuspended = status === 'suspended';
  const statusId = isActive ? 'active' : isSuspended ? 'suspended' : 'pending';
  return (
    <Chip
      label={t(`usersPage.status.${statusId}`)}
      size="small"
      color={isActive ? 'success' : isSuspended ? 'error' : 'warning'}
      variant="outlined"
      sx={{ fontSize: 11, fontWeight: 600 }}
    />
  );
}

// ─── Sort helpers ─────────────────────────────────────────────────────────────

type SortField = 'displayName' | 'email' | 'status' | 'roles';
type SortDir = 'asc' | 'desc';

function sortMembers(list: MembershipRecord[], field: SortField, dir: SortDir) {
  return [...list].sort((a, b) => {
    let va = '';
    let vb = '';
    if (field === 'displayName') {
      va = (a.displayName || a.email || '').toLowerCase();
      vb = (b.displayName || b.email || '').toLowerCase();
    } else if (field === 'email') {
      va = (a.email || '').toLowerCase();
      vb = (b.email || '').toLowerCase();
    } else if (field === 'status') {
      va = a.status;
      vb = b.status;
    } else if (field === 'roles') {
      va = a.roles[0] ?? '';
      vb = b.roles[0] ?? '';
    }
    const cmp = va.localeCompare(vb);
    return dir === 'asc' ? cmp : -cmp;
  });
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function UsersPage() {
  const { t } = useTranslation('sis');
  const confirm = useConfirm();
  const { currentTenantId } = useTenantStore();
  const [members, setMembers] = useState<MembershipRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [editMember, setEditMember] = useState<MembershipRecord | null>(null);
  const [activityMember, setActivityMember] = useState<MembershipRecord | null>(null);
  const { isPlatformAdmin } = useAuth();
  const [removingUid, setRemovingUid] = useState<string | null>(null);
  const [resendingUid, setResendingUid] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // ── Filters & sort state ──────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('displayName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Only system users (staff) — pure students live in the Personas page.
  const systemMembers = useMemo(() => members.filter(isSystemUser), [members]);

  // ── Derived: filtered + sorted list ──────────────────────────────────────
  const displayedMembers = useMemo(() => {
    let list = systemMembers;
    // text search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (m) =>
          (m.displayName || '').toLowerCase().includes(q) ||
          (m.email || '').toLowerCase().includes(q),
      );
    }
    // role filter
    if (filterRole !== 'all') {
      list = list.filter((m) => m.roles.includes(filterRole as never));
    }
    // status filter
    if (filterStatus !== 'all') {
      list = list.filter((m) => m.status === filterStatus);
    }
    return sortMembers(list, sortField, sortDir);
  }, [systemMembers, search, filterRole, filterStatus, sortField, sortDir]);

  const hasActiveFilters = search.trim() || filterRole !== 'all' || filterStatus !== 'all';

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function clearFilters() {
    setSearch('');
    setFilterRole('all');
    setFilterStatus('all');
  }

  async function loadMembers() {
    if (!currentTenantId) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchMemberships(currentTenantId);
      setMembers(data);
    } catch (e: unknown) {
      setError(errorText(e, t('usersPage.errors.load')));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMembers();
  }, [currentTenantId]);

  async function handleRemove(uid: string) {
    if (!currentTenantId) return;
    if (!(await confirm({ message: t('usersPage.confirmRemove'), confirmColor: 'error' }))) return;
    setRemovingUid(uid);
    try {
      await usersApi.remove({ tenantId: currentTenantId, uid });
      await loadMembers();
    } catch (e: unknown) {
      setErrorMsg(errorText(e, t('usersPage.errors.remove')));
    } finally {
      setRemovingUid(null);
    }
  }

  async function handleResend(uid: string) {
    if (!currentTenantId) return;
    setResendingUid(uid);
    try {
      await usersApi.resendInvite({ tenantId: currentTenantId, uid });
      setSuccessMsg(t('usersPage.resendSuccess'));
    } catch (e: unknown) {
      setErrorMsg(errorText(e, t('usersPage.errors.resend')));
    } finally {
      setResendingUid(null);
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        mb={3}
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {t('usersPage.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('usersPage.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddRounded />}
          onClick={() => setShowInvite(true)}
          sx={{ borderRadius: 2, px: 2.5, flexShrink: 0 }}
        >
          {t('usersPage.inviteButton')}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Filters toolbar ── */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        {/* Search */}
        <TextField
          id="users-search"
          size="small"
          placeholder={t('usersPage.filters.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearRounded fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {/* Role filter */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="filter-role-label">{t('usersPage.filters.roleLabel')}</InputLabel>
          <Select
            labelId="filter-role-label"
            id="filter-role"
            value={filterRole}
            label={t('usersPage.filters.roleLabel')}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <MenuItem value="all">{t('usersPage.filters.allRoles')}</MenuItem>
            {/* No 'student' here — pure students aren't system users (see isSystemUser). */}
            {ALL_ROLES.filter((r) => r.value !== 'student').map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {t(r.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="filter-status-label">{t('usersPage.filters.statusLabel')}</InputLabel>
          <Select
            labelId="filter-status-label"
            id="filter-status"
            value={filterStatus}
            label={t('usersPage.filters.statusLabel')}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="all">{t('usersPage.filters.allStatuses')}</MenuItem>
            <MenuItem value="active">{t('usersPage.status.active')}</MenuItem>
            <MenuItem value="suspended">{t('usersPage.status.suspended')}</MenuItem>
            <MenuItem value="pending">{t('usersPage.status.pending')}</MenuItem>
          </Select>
        </FormControl>

        {/* Result count + clear */}
        <Stack direction="row" alignItems="center" gap={1} ml="auto">
          <Chip
            icon={<FilterListRounded sx={{ fontSize: '14px !important' }} />}
            label={t('usersPage.filters.resultCount', {
              shown: displayedMembers.length,
              total: systemMembers.length,
            })}
            size="small"
            variant="outlined"
            color={hasActiveFilters ? 'primary' : 'default'}
          />
          {hasActiveFilters && (
            <Tooltip title={t('usersPage.filters.clear')}>
              <IconButton size="small" onClick={clearFilters}>
                <ClearRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                  <TableSortLabel
                    active={sortField === 'displayName'}
                    direction={sortField === 'displayName' ? sortDir : 'asc'}
                    onClick={() => handleSort('displayName')}
                  >
                    {t('usersPage.table.user')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                  <TableSortLabel
                    active={sortField === 'roles'}
                    direction={sortField === 'roles' ? sortDir : 'asc'}
                    onClick={() => handleSort('roles')}
                  >
                    {t('usersPage.table.roles')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                  {t('usersPage.table.person')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                  <TableSortLabel
                    active={sortField === 'status'}
                    direction={sortField === 'status' ? sortDir : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    {t('usersPage.table.status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>
                  {t('usersPage.table.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedMembers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {hasActiveFilters ? t('usersPage.empty.filtered') : t('usersPage.empty.none')}
                  </TableCell>
                </TableRow>
              )}
              {displayedMembers.map((member) => (
                <TableRow key={member.uid} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Avatar
                        {...(member.photoURL ? { src: member.photoURL } : {})}
                        sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}
                      >
                        {(member.displayName || member.email || member.uid)
                          .slice(0, 2)
                          .toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {member.displayName || member.uid}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {member.email || member.uid}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {member.roles.map((role) => {
                        const meta = ALL_ROLES.find((r) => r.value === role);
                        const colorKey = ROLE_COLORS[role] ?? 'default';
                        return (
                          <Tooltip key={role} title={meta ? t(meta.descriptionKey) : role}>
                            <Chip
                              label={meta ? t(meta.labelKey) : role}
                              size="small"
                              color={
                                colorKey as
                                  | 'error'
                                  | 'warning'
                                  | 'info'
                                  | 'secondary'
                                  | 'success'
                                  | 'default'
                              }
                              variant="outlined"
                              sx={{ fontSize: 11, fontWeight: 600 }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {member.personId ? (
                      <Chip
                        icon={<LinkRounded sx={{ fontSize: 14 }} />}
                        label={t('usersPage.person.linked')}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontSize: 11 }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {t('usersPage.person.unlinked')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={member.status} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" justifyContent="flex-end" gap={0.5}>
                      {/* SOLO PLATAFORMA (lo verifica el servidor, y la
                          consulta queda auditada): acompañar el ingreso de
                          alguien, sin dárselo a la dirección del seminario. */}
                      {isPlatformAdmin && (
                        <Tooltip title={t('usersPage.activity.open')}>
                          <IconButton
                            size="small"
                            aria-label={t('usersPage.activity.open')}
                            onClick={() => setActivityMember(member)}
                          >
                            <TimelineRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t('usersPage.rowActions.resend')}>
                        <IconButton
                          size="small"
                          onClick={() => handleResend(member.uid)}
                          disabled={resendingUid === member.uid}
                          color="info"
                        >
                          {resendingUid === member.uid ? (
                            <CircularProgress size={16} />
                          ) : (
                            <MarkEmailReadRounded fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('usersPage.rowActions.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => setEditMember(member)}
                          color="primary"
                        >
                          <EditRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('usersPage.rowActions.remove')}>
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(member.uid)}
                          disabled={removingUid === member.uid}
                          color="error"
                        >
                          {removingUid === member.uid ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteRounded fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogs */}
      {currentTenantId && (
        <InviteDialog
          open={showInvite}
          tenantId={currentTenantId}
          onClose={() => setShowInvite(false)}
          onDone={loadMembers}
        />
      )}
      {isPlatformAdmin && (
        <UserActivityDialog
          uid={activityMember?.uid ?? null}
          name={activityMember?.displayName || activityMember?.email || ''}
          onClose={() => setActivityMember(null)}
        />
      )}
      {currentTenantId && (
        <EditRolesDialog
          // Defensa adicional: un diálogo nuevo por cuenta.
          key={editMember?.uid ?? 'none'}
          member={editMember}
          tenantId={currentTenantId}
          members={members}
          onClose={() => setEditMember(null)}
          onDone={loadMembers}
        />
      )}

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={6000}
        onClose={() => setErrorMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
